import { EventEmitter } from '@angular/core';
import { COLUMN_DATA_TYPE } from '@emma-components/emma-table/emma-table.helpers';
import {
  NO_CHANNEL_VALUE,
  ORGANIC_CAMPAIGN,
  ORGANIC_CHANNEL,
  ORGANIC_PROVIDER,
  TRACKER_TYPE,
} from 'emma-common-ts/emma';
import { EMMATableColumn } from '@emma-components/emma-table/emma-table.types';

const commonColumns = (options: {
  currency: string;
  isRetargeting: boolean;
  hasSales: boolean;
  hasRegisters: boolean;
}): EMMATableColumn[] => [
  {
    prop: 'cpa',
    name: $localize`CPA`,
    width: 10,
    dataType: COLUMN_DATA_TYPE.CURRENCY,
    unit: options.currency,
  },
  {
    prop: 'clicks',
    name: $localize`Clics`,
    width: 8,
    dataType: COLUMN_DATA_TYPE.NUMBER,
  },
  {
    prop: 'cpc',
    name: $localize`CPC`,
    width: 5,
    dataType: COLUMN_DATA_TYPE.CURRENCY,
    unit: options.currency,
  },
  {
    prop: 'cost',
    name: $localize`Coste`,
    width: 8,
    dataType: COLUMN_DATA_TYPE.CURRENCY,
    unit: options.currency,
  },
  {
    prop: 'installs',
    name: $localize`Instalaciones`,
    width: 12,
    dataType: COLUMN_DATA_TYPE.NUMBER,
  },
  {
    prop: 'cpi',
    name: $localize`CPI`,
    width: 5,
    dataType: COLUMN_DATA_TYPE.CURRENCY,
    unit: options.currency,
  },
  ...(options.isRetargeting ? getRetargetingColumns() : []),
  {
    prop: 'itr',
    name: $localize`ITR`,
    width: 5,
    dataType: COLUMN_DATA_TYPE.PERCENT,
  },
  ...(options.hasRegisters
    ? [
        {
          prop: 'leads',
          name: $localize`Registros`,
          width: 10,
          dataType: COLUMN_DATA_TYPE.NUMBER,
        },
        {
          prop: 'cpl',
          name: $localize`CPL`,
          description: $localize`Cost per lead`,
          width: 5,
          dataType: COLUMN_DATA_TYPE.CURRENCY,
          unit: options.currency,
        },
      ]
    : []),
  ...(options.hasSales
    ? [
        {
          prop: 'sales',
          name: $localize`Ventas`,
          width: 10,
          dataType: COLUMN_DATA_TYPE.NUMBER,
        },
        {
          prop: 'cps',
          name: $localize`CPS`,
          width: 5,
          dataType: COLUMN_DATA_TYPE.CURRENCY,
          unit: options.currency,
        },
      ]
    : []),
];

const columnAttribution = (trackerType?: TRACKER_TYPE): EMMATableColumn[] => {
  if (trackerType === TRACKER_TYPE.ALL) {
    return [
      {
        prop: 'attributionType',
        name: $localize`Atribución`,
        width: 5,
        dataType: COLUMN_DATA_TYPE.STRING,
      },
    ];
  }
  return [];
};

export const getApptrackerBaseColumns = <T>(options: {
  nameCellAction: EventEmitter<T>;
  currency: string;
  trackerType: TRACKER_TYPE;
  hasRegisters: boolean;
  hasSales: boolean;
}): EMMATableColumn[] => [
  {
    prop: 'cid',
    name: $localize`ID`,
    width: 5,
    hiddenByDefault: true,
  },
  ...columnAttribution(options.trackerType),
  {
    prop: '__name',
    name: $localize`Nombre`,
    width: 30,
    dataType: COLUMN_DATA_TYPE.ACTION_ICON_LIST,
    $rowCellAction: options.nameCellAction,
  },
  ...commonColumns({ ...options, isRetargeting: options.trackerType !== TRACKER_TYPE.BASIC }),
  {
    prop: 'startDate',
    name: $localize`Fecha de inicio`,
    width: 9,
    dataType: COLUMN_DATA_TYPE.DATE,
  },
  {
    prop: 'endDate',
    name: $localize`Fecha de fin`,
    width: 9,
    dataType: COLUMN_DATA_TYPE.DATE,
  },
  {
    prop: 'entws',
    name: $localize`Número de fuentes`,
    width: 5,
    dataType: COLUMN_DATA_TYPE.LIST_LENGTH,
    hiddenByDefault: true,
    sortable: false,
  },
  {
    prop: 'revenue',
    name: $localize`Beneficio`,
    width: 10,
    dataType: COLUMN_DATA_TYPE.CURRENCY,
    unit: options.currency,
  },
];

export const getSourcesBaseColumns = <T>(options: {
  nameCellAction: EventEmitter<T>;
  currency: string;
  trackerType: TRACKER_TYPE;
  hasRegisters: boolean;
  hasSales: boolean;
}): EMMATableColumn[] => [
  {
    prop: 'sid',
    name: $localize`ID`,
    width: 10,
    hiddenByDefault: true,
  },
  ...columnAttribution(options.trackerType),
  {
    prop: '__name',
    name: $localize`Nombre`,
    width: 10,
    dataType: COLUMN_DATA_TYPE.ACTION_ICON_LIST,
    $rowCellAction: options.nameCellAction,
  },
  {
    prop: 'campaignName',
    name: $localize`Campaña`,
    width: 30,
    dataType: COLUMN_DATA_TYPE.STRING,
  },
  {
    prop: 'provider',
    name: $localize`Fuente de medios`,
    width: 8,
  },
  {
    prop: 'powlink',
    name: $localize`Powlink`,
    hiddenByDefault: false,
    dataType: COLUMN_DATA_TYPE.URL,
    width: 25,
  },
  {
    prop: 'pwlnk',
    name: $localize`pwlnk`,
    dataType: COLUMN_DATA_TYPE.URL,
    hiddenByDefault: false,
    width: 15,
  },
  {
    prop: '__qrUrl',
    name: $localize`QR`,
    dataType: COLUMN_DATA_TYPE.QR_CODE,
    width: 5,
  },
  ...commonColumns({ ...options, isRetargeting: options.trackerType !== TRACKER_TYPE.BASIC }),
  {
    prop: 'mode',
    name: $localize`Modelo de pago`,
    width: 8,
  },
];

export const getApptrackerChannelsBaseColumns = <T>(
  nameCellAction: EventEmitter<T>,
  currency: string,
  trackerType: TRACKER_TYPE
): EMMATableColumn[] => [
  {
    prop: '__channel',
    name: $localize`Nombre`,
    width: 30,
    dataType: COLUMN_DATA_TYPE.ACTION,
    $rowCellAction: nameCellAction,
  },
  ...commonColumns({
    currency,
    isRetargeting: trackerType !== TRACKER_TYPE.BASIC,
    hasRegisters: true,
    hasSales: true,
  }),
  {
    prop: 'entws',
    name: $localize`Número de fuentes`,
    width: 5,
    dataType: COLUMN_DATA_TYPE.LIST_LENGTH,
    hiddenByDefault: true,
    sortable: false,
  },
];

export const getRetargetingColumns = (): EMMATableColumn[] => [
  {
    prop: 'engagement',
    name: $localize`Engagement`,
    width: 5,
    dataType: COLUMN_DATA_TYPE.NUMBER,
  },
];

export const localizeProviderName = (name: string): string => {
  return name === ORGANIC_PROVIDER ? $localize`Orgánico` : name;
};

export const localizeCampaignName = (name: string, channel: string): string => {
  return name === ORGANIC_CAMPAIGN && channel === ORGANIC_CHANNEL ? $localize`Instalaciones Orgánicas` : name;
};

export const localizeChannelName = (name: string): string => {
  if (name === NO_CHANNEL_VALUE) {
    return $localize`Sin canal`;
  } else if (name === 'Organic') {
    return $localize`Orgánico`;
  }
  return name;
};

export const localizeAiAnalysis = {
  BUTTON_TEXT: $localize`Analizar datos`,
  ANALYZING: $localize`Analizando...`,
};
